// src/pages/CreateCompanyPage.js
import { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";

// Huvudkomponent för att skapa nytt företag
export default function CreateCompanyPage() {
  // State för formulärdata
  const [formData, setFormData] = useState({
    companyName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    orgNumber: "",
    address: ""
  });

  // State för laddning, felmeddelanden och succémeddelanden
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  // Hanterar ändringar i formulärfält
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // Rensa fel- och succémeddelanden när användaren skriver
    setError("");
    setSuccess("");
  };

  // Hanterar formulärinskickning
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Hämta och trimma formulärdata
    const companyName = formData.companyName.trim();
    const email = formData.email.trim();
    const password = formData.password;
    const confirmPassword = formData.confirmPassword;
    const phone = formData.phone.trim();
    const orgNumber = formData.orgNumber.trim();
    const address = formData.address.trim();

    // Validering av obligatoriska fält
    if (!companyName) {
      setError("Företagsnamn måste fyllas i");
      setLoading(false);
      return;
    }

    if (!email || !email.includes("@")) {
      setError("Ange en giltig e-postadress");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Lösenordet måste vara minst 6 tecken");
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Lösenorden matchar inte");
      setLoading(false);
      return;
    }

    try {
      // Hämta Firebase Functions
      const functions = getFunctions();
      const createCompany = httpsCallable(functions, "createCompany");

      // Förbered data för Cloud Function
      const payload = {
        companyName,
        email,
        password,
        phone: phone || "",
        orgNumber: orgNumber || "",
        address: address || ""
      };

      console.log("🚀 Skickar data till Cloud Function:", payload);

      // Anropa Cloud Function
      const result = await createCompany(payload);

      console.log("✅ Resultat från Cloud Function:", result.data);

      // Hantera lyckat svar
      if (result.data?.success) {
        setSuccess(`✅ Företag "${companyName}" har skapats!`);

        // Återställ formuläret
        setFormData({
          companyName: "",
          email: "",
          password: "",
          confirmPassword: "",
          phone: "",
          orgNumber: "",
          address: ""
        });

        // Omdirigera till inloggning efter 3 sekunder
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setError(result.data?.error || "Kunde inte skapa företag");
      }
    } catch (error) {
      console.error("❌ Fel vid anrop till Cloud Function:", error);

      // Hantera olika typer av fel från Cloud Function
      let errorMessage = "Kunde inte skapa företag";

      if (error?.code === "functions/unauthenticated") {
        errorMessage = "Du måste vara inloggad för att skapa företag";
      } else if (error?.code === "functions/permission-denied") {
        errorMessage = "Du har inte behörighet att skapa företag";
      } else if (error?.code === "functions/internal") {
        errorMessage = "Internt serverfel. Försök igen senare.";
      } else if (error?.code === "functions/invalid-argument") {
        errorMessage = "Ogiltiga uppgifter. Kontrollera att alla fält är korrekt ifyllda.";
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Specifika felmeddelanden för e-post och lösenord
      if (
        errorMessage.includes("email-already-exists") ||
        errorMessage.includes("E-postadressen används redan")
      ) {
        errorMessage = "E-postadressen är redan registrerad. Använd en annan e-postadress.";
      }

      if (errorMessage.toLowerCase().includes("password")) {
        errorMessage = "Lösenordet måste vara minst 6 tecken och vara starkt nog.";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Stilar för komponenten
  const styles = {
    container: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #f6f7ff 0%, #eef1ff 100%)",
      padding: "20px"
    },
    card: {
      maxWidth: "500px",
      width: "100%",
      background: "white",
      borderRadius: "24px",
      padding: "40px",
      boxShadow: "0 20px 50px rgba(31, 41, 55, 0.1)",
      border: "1px solid rgba(255,255,255,0.7)"
    },
    title: {
      margin: "0 0 8px 0",
      fontSize: "30px",
      fontWeight: "800",
      color: "#252b42",
      textAlign: "center"
    },
    subtitle: {
      marginBottom: "30px",
      color: "#8f97ad",
      fontSize: "14px",
      textAlign: "center"
    },
    errorBox: {
      backgroundColor: "#fff0f0",
      border: "1px solid #ffcdcd",
      borderRadius: "8px",
      padding: "12px",
      marginBottom: "20px",
      color: "#e5484d",
      fontSize: "14px"
    },
    successBox: {
      backgroundColor: "#f0fdf4",
      border: "1px solid #bbf7d0",
      borderRadius: "8px",
      padding: "12px",
      marginBottom: "20px",
      color: "#166534",
      fontSize: "14px"
    },
    successSubtext: {
      fontSize: "12px",
      marginTop: "5px",
      color: "#15803d"
    },
    formGroup: {
      marginBottom: "15px"
    },
    label: {
      display: "block",
      marginBottom: "5px",
      fontWeight: "500",
      color: "#374151"
    },
    input: {
      width: "100%",
      padding: "12px 16px",
      borderRadius: "12px",
      border: "1px solid #e5e7eb",
      fontSize: "14px",
      boxSizing: "border-box",
      transition: "border-color 0.2s"
    },
    button: {
      width: "100%",
      border: "none",
      borderRadius: "12px",
      padding: "14px",
      background: loading
        ? "linear-gradient(135deg, #b8a9e6, #9b8ad9)"
        : "linear-gradient(135deg, #8d43ff, #6323ef)",
      color: "#fff",
      fontSize: "16px",
      fontWeight: "700",
      cursor: loading ? "not-allowed" : "pointer",
      boxShadow: loading ? "none" : "0 8px 20px rgba(99,35,239,0.3)",
      transition: "all 0.2s ease",
      opacity: loading ? 0.7 : 1
    },
    footer: {
      textAlign: "center",
      marginTop: "20px",
      fontSize: "13px",
      color: "#8f97ad"
    },
    link: {
      color: "#6323ef",
      textDecoration: "none",
      fontWeight: "500"
    }
  };

  // Fokus-hanterare för inputs
  const handleFocus = (e) => {
    e.target.style.borderColor = "#8d43ff";
  };

  const handleBlur = (e) => {
    e.target.style.borderColor = "#e5e7eb";
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Sidhuvud */}
        <h1 style={styles.title}>
          🏢 Skapa nytt företag
        </h1>

        <p style={styles.subtitle}>
          Fyll i uppgifterna nedan för att skapa ett nytt företagskonto
        </p>

        {/* Felmeddelande */}
        {error && (
          <div style={styles.errorBox}>
            ❌ {error}
          </div>
        )}

        {/* Succémeddelande */}
        {success && (
          <div style={styles.successBox}>
            {success}
            <div style={styles.successSubtext}>
              Du omdirigeras till inloggning...
            </div>
          </div>
        )}

        {/* Formulär */}
        <form onSubmit={handleSubmit}>
          {/* Företagsnamn */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Företagsnamn *
            </label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              required
              placeholder="Ex: Mitt Företag AB"
              style={styles.input}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>

          {/* Administratörs e-post */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Administratörs e-post *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="admin@foretag.se"
              style={styles.input}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>

          {/* Lösenord */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Lösenord * (minst 6 tecken)
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
              placeholder="••••••"
              style={styles.input}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>

          {/* Bekräfta lösenord */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Bekräfta lösenord *
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder="••••••"
              style={styles.input}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>

          {/* Telefon (valfritt) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Telefon (valfritt)
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="070-1234567"
              style={styles.input}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>

          {/* Organisationsnummer (valfritt) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Organisationsnummer (valfritt)
            </label>
            <input
              type="text"
              name="orgNumber"
              value={formData.orgNumber}
              onChange={handleChange}
              placeholder="556677-8899"
              style={styles.input}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>

          {/* Adress (valfritt) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Adress (valfritt)
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Gatugatan 1, 123 45 Stockholm"
              style={styles.input}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>

          {/* Submit-knapp */}
          <button
            type="submit"
            disabled={loading}
            style={styles.button}
          >
            {loading ? "Skapar företag..." : "Skapa företag 🚀"}
          </button>
        </form>

        {/* Länk till inloggning */}
        <p style={styles.footer}>
          Har du redan ett konto?{" "}
          <a
            href="/login"
            style={styles.link}
          >
            Logga in här
          </a>
        </p>
      </div>
    </div>
  );
}